/**
 * LotowheelSimulator — modal de simulación del ecosistema lotowheel.
 *
 * Se invoca desde el modal de configuración del sorteo. Pega N veces al
 * backend con la config actual y mide convergencia + experiencia jugador,
 * sin tocar DB real (worker thread en backend).
 *
 * Estructura del archivo:
 *   1. CICLO MODAL          open / close / saveAndClose.
 *   2. RESOLVER PICKER      populate selects + detectar preset actual.
 *   3. SLIDERS Y ESCENARIO  controles de simulación (volatilidad, etc).
 *   4. EJECUCIÓN            runSimulation() + overlay de progreso.
 *   5. CHARTS               builders de datasets + render Chart.js.
 *   6. KPIs                 panel de métricas comparables.
 *
 * Backend: /api/admin/lotowheel/simular. Gráficas Chart.js: líneas por
 * local + experiencia jugador (peor/mejor/promedio) + RTP + drawdown.
 */

import Chart from 'chart.js/auto';
import { LotowheelModule, TIPO_DE_RUEDA_INICIAL_PARA_FORM_ADMIN } from './lotowheel.js';
import { ROULETTE_TYPES as TIPOS_DE_RUEDA_SSOT } from '@shared/config/games/lotowheel/roulette-numbers-order-config.js';
import { App, Api } from '../../../admin.js';
import { t } from '../../../services/i18n-admin.js';

const LOCALE_COLORS = [
    '#4fc3f7',
    '#81c784',
    '#ff9800',
    '#ce93d8',
    '#ef5350',
    '#26c6da',
    '#aed581',
    '#ffa726',
    '#ba68c8',
    '#ec407a',
    '#42a5f5',
    '#66bb6a',
    '#ffca28',
    '#ab47bc',
    '#f44336',
    '#29b6f6',
    '#9ccc65',
    '#ffd54f',
    '#7e57c2',
    '#e91e63',
];

export const LotowheelSimulator = {
    _parentConfigId: null,
    _resolverName: null,
    _resolverConfig: null,
    _sliders: null,
    _chart: null,

    /**
     * Abre el modal simulador desde el modal LotoWheel.
     * Guarda estado del padre para restaurarlo al cerrar.
     */
    // ═══════════════════════════════════════════════════════════════════════
    // CICLO MODAL — open desde lotowheel.js, close limpia state, save aplica.
    // ═══════════════════════════════════════════════════════════════════════

    open(resolverName, resolverConfig, sliders) {
        this._parentConfigId = document.getElementById('lotowheelConfigId').value;
        this._resolverName = resolverName;
        this._resolverConfig = resolverConfig;
        this._sliders = sliders;

        App.openModal('modalSimulator');
        this._populateDefaults();
    },

    /**
     * Cierra el simulador y restaura el modal LotoWheel.
     */
    close() {
        this._destroyCharts();

        // Ocultar resultados para próxima apertura
        const resultsSection = document.getElementById('simResultsSection');
        if (resultsSection) resultsSection.style.display = 'none';

        // Volver al modal padre sin re-fetchear (conserva ediciones del usuario)
        if (this._parentConfigId) {
            App.openModal('modalLotowheelConfig');
            // Ir a la tab de Resolución
            const tabs = document.querySelectorAll('#modalLotowheelConfig .modal-tab');
            const panels = document.querySelectorAll('#modalLotowheelConfig .tab-panel');
            for (let i = 0; i < tabs.length; i++) {
                const isResolver = tabs[i].dataset.tab === 'resolver';
                tabs[i].classList.toggle('active', isResolver);
                panels[i].classList.toggle('active', isResolver);
            }
            this._parentConfigId = null;
        } else {
            App.closeModal();
        }
    },

    /**
     * Guarda la config del sorteo (desde el modal padre oculto) y cierra todo.
     */
    async saveAndClose() {
        this._destroyCharts();
        const resultsSection = document.getElementById('simResultsSection');
        if (resultsSection) resultsSection.style.display = 'none';
        this._parentConfigId = null;

        await LotowheelModule.saveLotowheelConfig();
    },

    /**
     * Construye el mapa resolver_name → displayName desde el cache.
     */
    // ═══════════════════════════════════════════════════════════════════════
    // RESOLVER PICKER — populate selects principal + comparación + presets.
    // ═══════════════════════════════════════════════════════════════════════

    _resolverDisplayNames() {
        const map = {};
        const resolvers = LotowheelModule._resolverCache || [];
        for (let i = 0; i < resolvers.length; i++) {
            map[resolvers[i].name] = resolvers[i].displayName;
        }
        return map;
    },

    /**
     * Detecta si los sliders actuales coinciden con algún preset.
     * @returns {Object|null} El preset que coincide, o null si fue modificado.
     */
    _detectarPresetActual() {
        if (!this._sliders) return null;
        const presets = LotowheelModule._resolverPresetsCache || [];
        for (let i = 0; i < presets.length; i++) {
            const p = presets[i];
            if (p.resolver_name !== this._resolverName || !p.sliders) continue;
            let coincide = true;
            for (const key of Object.keys(this._sliders)) {
                if (p.sliders[key] !== this._sliders[key]) {
                    coincide = false;
                    break;
                }
            }
            if (coincide) return p;
        }
        return null;
    },

    /**
     * Pobla el select PRINCIPAL (simular): "Modificado" o preset actual + todos los presets.
     */
    _populateMainSelect(select) {
        select.innerHTML = '';
        const displayNames = this._resolverDisplayNames();
        const presets = LotowheelModule._resolverPresetsCache || [];
        const presetActual = this._detectarPresetActual();

        // Si los sliders no coinciden con ningún preset → opción "Modificado" seleccionada
        if (!presetActual) {
            const tipoLabel = displayNames[this._resolverName] || this._resolverName;
            const modOpt = document.createElement('option');
            modOpt.value = 'current';
            modOpt.textContent = t('Configuración modificada') + ' (' + tipoLabel + ')';
            modOpt.selected = true;
            select.appendChild(modOpt);
        }

        // Todos los presets agrupados por resolver, con tipo entre paréntesis
        const presetsByResolver = {};
        for (let i = 0; i < presets.length; i++) {
            const rn = presets[i].resolver_name;
            if (!presetsByResolver[rn]) presetsByResolver[rn] = [];
            presetsByResolver[rn].push(presets[i]);
        }
        for (const rn of Object.keys(presetsByResolver)) {
            const grp = document.createElement('optgroup');
            grp.label = displayNames[rn] || rn;
            const items = presetsByResolver[rn];
            for (let i = 0; i < items.length; i++) {
                const p = items[i];
                const opt = document.createElement('option');
                opt.value = 'preset:' + p.id;
                opt.textContent = p.nombre + ' (' + (displayNames[p.resolver_name] || p.resolver_name) + ')';
                if (presetActual && presetActual.id === p.id) opt.selected = true;
                grp.appendChild(opt);
            }
            select.appendChild(grp);
        }
    },

    /**
     * Pobla el select de COMPARACIÓN: solo presets concretos, sin resolvers genéricos.
     */
    _populateCompareSelect(select) {
        select.innerHTML = '<option value="">' + t('Sin comparación') + '</option>';
        const displayNames = this._resolverDisplayNames();
        const presets = LotowheelModule._resolverPresetsCache || [];

        const presetsByResolver = {};
        for (let i = 0; i < presets.length; i++) {
            const rn = presets[i].resolver_name;
            if (!presetsByResolver[rn]) presetsByResolver[rn] = [];
            presetsByResolver[rn].push(presets[i]);
        }
        for (const rn of Object.keys(presetsByResolver)) {
            const grp = document.createElement('optgroup');
            grp.label = displayNames[rn] || rn;
            const items = presetsByResolver[rn];
            for (let i = 0; i < items.length; i++) {
                const p = items[i];
                const opt = document.createElement('option');
                opt.value = 'preset:' + p.id;
                opt.textContent = p.nombre + ' (' + (displayNames[p.resolver_name] || p.resolver_name) + ')';
                grp.appendChild(opt);
            }
            select.appendChild(grp);
        }
    },

    /**
     * Parsea un valor del select resolver/preset en { resolverName, config }.
     */
    _parseResolverSelectValue(val) {
        if (!val) return null;
        if (val.startsWith('preset:')) {
            const presetId = Number(val.split(':')[1]);
            const preset = (LotowheelModule._resolverPresetsCache || []).find(p => p.id === presetId);
            if (preset)
                return {
                    resolverName: preset.resolver_name,
                    config: preset.config,
                    sliders: preset.sliders || null,
                    label: preset.nombre,
                };
        } else if (val.startsWith('resolver:')) {
            const name = val.split(':')[1];
            const resolver = (LotowheelModule._resolverCache || []).find(r => r.name === name);
            return { resolverName: name, config: null, label: resolver?.displayName || name };
        }
        return null;
    },

    /**
     * Cuando cambia el select principal del simulador.
     */
    onResolverChange() {
        const select = document.getElementById('simResolverSelect');
        if (!select) return;
        // "current" = configuración modificada (usa sliders/config que ya están en this._)
        if (select.value === 'current') return;
        const parsed = this._parseResolverSelectValue(select.value);
        if (parsed) {
            this._resolverName = parsed.resolverName;
            this._resolverConfig = parsed.config || {};
            this._sliders = parsed.sliders || null;
        }
    },

    // ═══════════════════════════════════════════════════════════════════════
    // SLIDERS Y ESCENARIO — controles de input (volatilidad, escenario, params).
    // ═══════════════════════════════════════════════════════════════════════

    _populateDefaults() {
        // Poblar select principal
        const mainSelect = document.getElementById('simResolverSelect');
        if (mainSelect) this._populateMainSelect(mainSelect);

        // Poblar select de comparación
        const compareSelect = document.getElementById('simCompareWith');
        if (compareSelect) this._populateCompareSelect(compareSelect);

        // Recalcular opciones de sorteos desde la SSOT (intervalo_segundos del juego)
        this._recalcularOpcionesSorteos();

        // Defaults de sliders de simulación
        this._updateSliderLabel('simParamLocales');
        this._updateSliderLabel('simParamJugadores');
        this.onSimParamChange('simParamTickets');
        this.onSimParamChange('simParamJugadasPorSorteo');
        this.onSimParamChange('simParamApuestasPorJugada');
        this.onSimParamChange('simParamMontoApuesta');
        this.onSimParamChange('simParamRetiroGanancia');
    },

    /**
     * Calcula las opciones del dropdown de sorteos a partir de intervalo_segundos (SSOT).
     * Asume 8h operativas/día × 70% ocupación real.
     */
    _recalcularOpcionesSorteos() {
        const HORAS_OPERATIVAS = 8;
        const OCUPACION = 0.7;
        const intervaloInput = document.getElementById('lotowheelConfigIntervalo');
        if (!intervaloInput || !intervaloInput.value) {
            console.error('[Simulador] lotowheelConfigIntervalo no encontrado — no se puede calcular sorteos');
            return;
        }
        const intervaloSeg = parseInt(intervaloInput.value);
        const sorteosPorDia = Math.round(((HORAS_OPERATIVAS * 3600) / intervaloSeg) * OCUPACION);

        const periodos = [
            { dias: 1, label: '~1 día' },
            { dias: 3, label: '~3 días' },
            { dias: 7, label: '~1 semana' },
            { dias: 14, label: '~2 semanas' },
            { dias: 30, label: '~1 mes' },
            { dias: 90, label: '~3 meses' },
        ];

        const select = document.getElementById('simParamSorteos');
        if (!select) return;

        const prevValue = parseInt(select.value) || 0;
        select.innerHTML = '';
        for (let i = 0; i < periodos.length; i++) {
            const sorteos = sorteosPorDia * periodos[i].dias;
            const opt = document.createElement('option');
            opt.value = sorteos;
            opt.textContent = sorteos.toLocaleString() + ' (' + periodos[i].label + ')';
            if (i === 2) opt.selected = true; // Default: 1 semana
            select.appendChild(opt);
        }

        // Si el usuario ya había seleccionado un valor, intentar restaurarlo
        if (prevValue > 0) {
            for (let i = 0; i < select.options.length; i++) {
                if (parseInt(select.options[i].value) === prevValue) {
                    select.selectedIndex = i;
                    break;
                }
            }
        }
    },

    _updateSliderLabel(inputId) {
        const input = document.getElementById(inputId);
        const label = document.getElementById(inputId + 'Val');
        if (input && label) label.textContent = input.value;
    },

    _escenarioDescripciones() {
        return {
            estable: t('Flujo constante de jugadores, sin eventos especiales'),
            local_nuevo: t('Arranque con muchas llegadas que se estabilizan gradualmente'),
            concentrada: t('Un grupo apuesta fuerte al mismo número durante un periodo'),
            recuperacion: t('La casa arranca con pérdida y simula hasta recuperar'),
        };
    },

    onEscenarioChange() {
        const select = document.getElementById('simParamEscenario');
        const desc = document.getElementById('simParamEscenarioDesc');
        if (select && desc) {
            desc.textContent = this._escenarioDescripciones()[select.value] || '';
        }
        const sorteosGroup = document.getElementById('simParamSorteosGroup');
        const deficitGroup = document.getElementById('simParamDeficitGroup');
        if (sorteosGroup && deficitGroup) {
            const isRecuperacion = select.value === 'recuperacion';
            sorteosGroup.style.display = isRecuperacion ? 'none' : '';
            deficitGroup.style.display = isRecuperacion ? '' : 'none';
        }
    },

    onSimParamChange(inputId) {
        const input = document.getElementById(inputId);
        const label = document.getElementById(inputId + 'Val');
        if (!input || !label) return;
        if (inputId === 'simParamTickets') {
            label.textContent = input.value + '%';
        } else if (inputId === 'simParamRetiroGanancia') {
            label.textContent = parseInt(input.value) === 0 ? 'Off' : input.value + 'x';
        } else {
            label.textContent = input.value;
        }
    },

    /**
     * Muestra/oculta overlay de progreso sobre el modal del simulador.
     */
    // ═══════════════════════════════════════════════════════════════════════
    // EJECUCIÓN — runSimulation() pega al backend + overlay de progreso.
    // ═══════════════════════════════════════════════════════════════════════

    _showProgressOverlay(descText, hasComparison) {
        let overlay = document.getElementById('simProgressOverlay');
        if (!overlay) {
            overlay = document.createElement('div');
            overlay.id = 'simProgressOverlay';
            overlay.style.cssText =
                'position:absolute;inset:0;background:rgba(0,0,0,0.75);display:flex;' +
                'align-items:center;justify-content:center;z-index:100;border-radius:inherit;' +
                'backdrop-filter:blur(3px);';
            const modal = document.getElementById('modalSimulator');
            if (modal) {
                modal.style.position = 'relative';
                modal.appendChild(overlay);
            }
        }

        // Inyectar CSS animation si no existe
        if (!document.getElementById('simSpinnerStyle')) {
            const style = document.createElement('style');
            style.id = 'simSpinnerStyle';
            style.textContent = '@keyframes simSpin{to{transform:rotate(360deg)}}';
            document.head.appendChild(style);
        }

        const desc = descText + (hasComparison ? ' &times; 2' : '');
        overlay.innerHTML =
            '<div style="text-align:center;color:#fff;">' +
            '<div style="width:36px;height:36px;border:3px solid rgba(255,255,255,0.2);' +
            'border-top-color:#fff;border-radius:50%;margin:0 auto 12px;' +
            'animation:simSpin 0.8s linear infinite;"></div>' +
            '<div style="font-size:14px;font-weight:600;">' + t('Simulando') + '</div>' +
            '<div style="font-size:12px;opacity:0.6;margin-top:4px;">' +
            desc +
            '</div>' +
            '<div style="font-size:12px;opacity:0.4;margin-top:6px;" id="simProgressTimer">0.0s</div>' +
            '</div>';

        overlay.style.display = 'flex';

        const startTime = performance.now();
        this._progressInterval = setInterval(() => {
            const timerEl = document.getElementById('simProgressTimer');
            if (timerEl) timerEl.textContent = ((performance.now() - startTime) / 1000).toFixed(1) + 's';
        }, 100);
    },

    _hideProgressOverlay() {
        if (this._progressInterval) {
            clearInterval(this._progressInterval);
            this._progressInterval = null;
        }
        const overlay = document.getElementById('simProgressOverlay');
        if (overlay) overlay.style.display = 'none';
    },

    /**
     * Ejecuta la simulación via API (worker thread en el backend).
     */
    async runSimulation() {
        const btn = document.getElementById('btnRunSimulation');
        const originalText = btn.textContent;
        btn.textContent = t('Simulando...');
        btn.disabled = true;

        try {
            const escenario = document.getElementById('simParamEscenario')?.value || 'estable';
            const isRecuperacion = escenario === 'recuperacion';

            const totalSorteos = isRecuperacion ? 10000 : parseInt(document.getElementById('simParamSorteos').value);
            const deficitInicial = isRecuperacion
                ? parseInt(document.getElementById('simParamDeficit').value) || 5000
                : 0;

            const localesCount = parseInt(document.getElementById('simParamLocales').value);
            const jugadoresPorLocal = parseInt(document.getElementById('simParamJugadores').value);
            const ticketPercent = parseInt(document.getElementById('simParamTickets').value);
            const jugadasPorSorteo = parseInt(document.getElementById('simParamJugadasPorSorteo').value);
            const apuestasPorJugada = parseInt(document.getElementById('simParamApuestasPorJugada').value);
            const montoApuesta = parseInt(document.getElementById('simParamMontoApuesta').value);
            const retiroGanancia = parseInt(document.getElementById('simParamRetiroGanancia').value);

            // Geometría de la rueda configurada en el modal padre.
            // Sin esto el backend caía a 37 (europea) y la simulación divergía
            // del resolver real para mini/mini18/american/triple_zero.
            // Tipo de rueda + totalNumbers DERIVADOS de las recetas (SSOT).
            // Antes habia una tabla manual TOTAL_NUMBERS_POR_TIPO que duplicaba
            // ROULETTE_TYPES[tipo].totalNumbers — bridge clasico que se borra:
            // agregar una receta nueva ya queda soportada sin tocar codigo.
            const tipoRuleta =
                document.getElementById('lotowheelConfigTipoRuleta')?.value || TIPO_DE_RUEDA_INICIAL_PARA_FORM_ADMIN;
            const cfgDeLaRueda = TIPOS_DE_RUEDA_SSOT[tipoRuleta];
            if (!cfgDeLaRueda) {
                throw new Error(
                    `[simulator] tipoRuleta "${tipoRuleta}" no existe en las recetas — abortar simulacion.`
                );
            }
            const totalNumbers = cfgDeLaRueda.totalNumbers;

            const payload = {
                totalSorteos,
                deficitInicial,
                totalNumbers,
                simParams: {
                    localesCount,
                    jugadoresPorLocal,
                    ticketPercent,
                    jugadasPorSorteo,
                    apuestasPorJugada,
                    montoApuesta,
                    retiroGanancia,
                    escenario: isRecuperacion ? 'estable' : escenario,
                    modoRecuperacion: isRecuperacion,
                },
            };

            // Resolver principal: leer del select (puede haber cambiado)
            const mainVal = document.getElementById('simResolverSelect')?.value;
            const mainParsed = this._parseResolverSelectValue(mainVal);
            if (mainParsed) {
                // VC: siempre usar sliders (del preset, del panel principal, o guardados al abrir)
                if (mainParsed.resolverName === 'volatilidad-controlada') {
                    const slidersToUse = mainParsed.sliders || this._sliders || LotowheelModule._collectSliderValues();
                    payload.sliders = slidersToUse;
                } else if (mainParsed.config) {
                    payload.resolverName = mainParsed.resolverName;
                    payload.config = mainParsed.config;
                } else {
                    payload.resolverName = mainParsed.resolverName;
                    payload.config = this._resolverConfig || {};
                }
            } else if (this._sliders) {
                payload.sliders = this._sliders;
            } else {
                payload.resolverName = this._resolverName;
                payload.config = this._resolverConfig;
            }

            // Comparar con otro resolver o preset
            const compareVal = document.getElementById('simCompareWith')?.value;
            const compareParsed = this._parseResolverSelectValue(compareVal);
            if (compareParsed) {
                // VC puro en comparación: usar sliders del panel principal
                if (
                    compareParsed.resolverName === 'volatilidad-controlada' &&
                    !compareParsed.sliders &&
                    !compareParsed.config
                ) {
                    payload.compareWith = {
                        sliders: this._sliders || LotowheelModule._collectSliderValues(),
                    };
                } else {
                    payload.compareWith = {
                        resolverName: compareParsed.resolverName,
                        config: compareParsed.config || undefined,
                        sliders: compareParsed.sliders || undefined,
                    };
                }
            }

            this._showProgressOverlay(
                isRecuperacion
                    ? t('Hasta recuperar $') + deficitInicial.toLocaleString()
                    : totalSorteos.toLocaleString() + ' sorteos',
                !!payload.compareWith
            );
            const result = await Api.callAdmin('/admin/resolver-profiles/simulate', 'POST', payload);

            // Mostrar resultados
            const resultsSection = document.getElementById('simResultsSection');
            if (resultsSection) resultsSection.style.display = '';

            const comparacion = result.comparacion || null;
            this._renderKpis(result.resultados, result.metadata, comparacion);
            this._renderAllCharts(result.resultados, result.resultados.convergencia, localesCount, comparacion);
        } catch (err) {
            App.toast(t('Error en simulacion: ') + err.message, 'error');
        } finally {
            this._hideProgressOverlay();
            btn.textContent = originalText;
            btn.disabled = false;
        }
    },

    _activeCharts: [],

    // ═══════════════════════════════════════════════════════════════════════
    // CHARTS — builders de datasets + render Chart.js (líneas + barras).
    // ═══════════════════════════════════════════════════════════════════════

    _destroyCharts() {
        for (let i = 0; i < this._activeCharts.length; i++) {
            this._activeCharts[i].destroy();
        }
        this._activeCharts = [];
        const container = document.getElementById('simChartsContainer');
        if (container) container.innerHTML = '';
    },

    /**
     * Crea un canvas dentro de un contenedor y devuelve el contexto 2d.
     */
    _createChartSlot(parent, title, height) {
        const wrapper = document.createElement('div');
        wrapper.style.cssText = 'flex:1; height:' + height + 'px; position:relative; min-width:0;';
        const label = document.createElement('div');
        label.style.cssText = 'font-size:12px; color:var(--text-secondary); margin-bottom:4px;';
        label.textContent = title;
        const canvas = document.createElement('canvas');
        wrapper.appendChild(label);
        wrapper.appendChild(canvas);
        parent.appendChild(wrapper);
        return canvas.getContext('2d');
    },

    /**
     * Crea una fila flex con gap para gráficas.
     */
    _createChartRow(container, marginTop) {
        const row = document.createElement('div');
        row.style.cssText = 'display:flex; gap:12px; margin-top:' + (marginTop || 0) + 'px;';
        container.appendChild(row);
        return row;
    },

    /**
     * Extrae convergencia de un resultado en arrays listos para Chart.js
     */
    _extractConvergenciaData(convergencia, localesCount) {
        const labels = [];
        const rtpGlobal = [];
        const rtpLocales = [];
        const playerP10 = [];
        const playerP25 = [];
        const playerP50 = [];
        const playerP75 = [];
        const playerP90 = [];

        for (let loc = 0; loc < localesCount; loc++) {
            rtpLocales.push([]);
        }

        for (let i = 0; i < convergencia.length; i++) {
            const snap = convergencia[i];
            labels.push(snap.sorteo);
            rtpGlobal.push(Number((snap.rtp * 100).toFixed(1)));

            for (let loc = 0; loc < localesCount; loc++) {
                const nodoId = loc + 1;
                const rtp = snap.rtpLocales ? snap.rtpLocales[nodoId] : 0;
                rtpLocales[loc].push(Number((rtp * 100).toFixed(1)));
            }

            const pe = snap.playerExperience || { p10: 0, p25: 0, p50: 0, p75: 0, p90: 0 };
            playerP10.push(Number((pe.p10 * 100).toFixed(1)));
            playerP25.push(Number((pe.p25 * 100).toFixed(1)));
            playerP50.push(Number((pe.p50 * 100).toFixed(1)));
            playerP75.push(Number((pe.p75 * 100).toFixed(1)));
            playerP90.push(Number((pe.p90 * 100).toFixed(1)));
        }

        return { labels, rtpGlobal, rtpLocales, playerP10, playerP25, playerP50, playerP75, playerP90 };
    },

    _chartOpts() {
        return {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
                legend: { labels: { color: '#ccc', font: { size: 10 }, usePointStyle: true, pointStyle: 'line' } },
                tooltip: { backgroundColor: 'rgba(0,0,0,0.85)' },
            },
            scales: {
                x: {
                    ticks: { color: '#888', maxTicksLimit: 10 },
                    grid: { color: 'rgba(255,255,255,0.05)' },
                },
                y: {
                    type: 'linear',
                    ticks: {
                        color: '#aaa',
                        callback: function (v) {
                            return v + '%';
                        },
                    },
                    grid: { color: 'rgba(255,255,255,0.05)' },
                },
            },
        };
    },

    // Plugin inline para mostrar valor encima de cada barra
    _barValuePlugin: {
        id: 'barValueLabels',
        afterDatasetsDraw(chart) {
            const ctx = chart.ctx;
            ctx.save();
            ctx.font = '11px sans-serif';
            ctx.fillStyle = '#ccc';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            for (let i = 0; i < chart.data.datasets.length; i++) {
                const meta = chart.getDatasetMeta(i);
                for (let j = 0; j < meta.data.length; j++) {
                    const val = chart.data.datasets[i].data[j];
                    if (val > 0) {
                        const bar = meta.data[j];
                        ctx.fillText(val, bar.x, bar.y - 3);
                    }
                }
            }
            ctx.restore();
        },
    },

    /**
     * Construye datasets de Casa (RTP por local + Global) a partir de datos extraídos.
     */
    _buildCasaDatasets(d, localesCount) {
        const datasets = [];
        for (let loc = 0; loc < localesCount; loc++) {
            datasets.push({
                label: t('Local ') + (loc + 1),
                data: d.rtpLocales[loc],
                borderColor: LOCALE_COLORS[loc % LOCALE_COLORS.length],
                backgroundColor: 'transparent',
                tension: 0.3,
                pointRadius: 0,
                borderWidth: 2,
            });
        }
        datasets.push({
            label: 'Global',
            data: d.rtpGlobal,
            borderColor: 'rgba(255,255,255,0.7)',
            backgroundColor: 'transparent',
            tension: 0.3,
            pointRadius: 0,
            borderWidth: 2.5,
        });
        return datasets;
    },

    /**
     * Construye datasets de Jugador (percentiles) a partir de datos extraídos.
     */
    _buildJugadorDatasets(d) {
        return [
            {
                label: t('10% peor'),
                data: d.playerP10,
                borderColor: '#ef5350',
                backgroundColor: 'transparent',
                tension: 0.3,
                pointRadius: 0,
                borderWidth: 1,
                borderDash: [4, 4],
            },
            {
                label: '25%',
                data: d.playerP25,
                borderColor: '#ff8a65',
                backgroundColor: 'transparent',
                tension: 0.3,
                pointRadius: 0,
                borderWidth: 1.5,
                borderDash: [6, 3],
            },
            {
                label: t('50% (típico)'),
                data: d.playerP50,
                borderColor: '#ffd54f',
                backgroundColor: 'transparent',
                tension: 0.3,
                pointRadius: 0,
                borderWidth: 2.5,
            },
            {
                label: '75%',
                data: d.playerP75,
                borderColor: '#81c784',
                backgroundColor: 'transparent',
                tension: 0.3,
                pointRadius: 0,
                borderWidth: 1.5,
                borderDash: [6, 3],
            },
            {
                label: t('90% mejor'),
                data: d.playerP90,
                borderColor: '#66bb6a',
                backgroundColor: 'transparent',
                tension: 0.3,
                pointRadius: 0,
                borderWidth: 1,
                borderDash: [4, 4],
            },
        ];
    },

    /**
     * Construye datasets de Balance/Drawdown a partir de convergencia.
     */
    _buildDrawdownDatasets(convergencia) {
        const labels = [];
        const balanceData = [];
        for (let i = 0; i < convergencia.length; i++) {
            labels.push(convergencia[i].sorteo);
            balanceData.push(Number((convergencia[i].casaBalance || 0).toFixed(0)));
        }
        let maxBal = balanceData[0];
        const ddData = [];
        for (let i = 0; i < balanceData.length; i++) {
            if (balanceData[i] > maxBal) maxBal = balanceData[i];
            ddData.push(balanceData[i] - maxBal);
        }
        return {
            labels: labels,
            datasets: [
                {
                    label: 'Balance',
                    data: balanceData,
                    borderColor: '#42a5f5',
                    backgroundColor: 'transparent',
                    tension: 0.3,
                    pointRadius: 0,
                    borderWidth: 2,
                },
                {
                    label: 'Drawdown',
                    data: ddData,
                    borderColor: '#ef5350',
                    backgroundColor: 'rgba(239, 83, 80, 0.1)',
                    tension: 0.3,
                    pointRadius: 0,
                    borderWidth: 1.5,
                    fill: true,
                },
            ],
        };
    },

    /**
     * Crea un Chart.js en un canvas dinámico y lo registra para limpieza.
     */
    _addChart(ctx, type, labels, datasets, opts, plugins) {
        const chart = new Chart(ctx, {
            type: type,
            plugins: plugins || [],
            data: { labels: labels, datasets: datasets },
            options: opts,
        });
        this._activeCharts.push(chart);
        return chart;
    },

    /**
     * Renderiza TODAS las gráficas dinámicamente en simChartsContainer.
     * - Líneas (Casa, Jugador, Jugador/Local, Balance): lado a lado si hay comparación
     * - Histogramas (Pagos, Jugadas): superpuestos siempre
     */
    _renderAllCharts(resultados, convergencia, localesCount, comparacion) {
        if (!convergencia || convergencia.length === 0) return;
        this._destroyCharts();

        const container = document.getElementById('simChartsContainer');
        if (!container) return;

        const mainName = this._getSelectDisplayName('simResolverSelect') || 'Principal';
        const chartOpts = this._chartOpts();
        const ddOpts = {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: 'index', intersect: false },
            plugins: {
                legend: { labels: { color: '#ccc', font: { size: 10 }, usePointStyle: true, pointStyle: 'line' } },
                tooltip: {
                    backgroundColor: 'rgba(0,0,0,0.85)',
                    callbacks: {
                        label: function (ctx) {
                            return ctx.dataset.label + ': ' + ctx.parsed.y.toFixed(2) + '%';
                        },
                    },
                },
            },
            scales: {
                x: { ticks: { color: '#888', maxTicksLimit: 10 }, grid: { color: 'rgba(255,255,255,0.05)' } },
                y: {
                    ticks: {
                        color: '#aaa',
                        callback: function (v) {
                            return v.toFixed(1) + '%';
                        },
                    },
                    grid: { color: 'rgba(255,255,255,0.05)' },
                },
            },
        };
        const barOpts = function (yLabel, hasLegend) {
            return {
                responsive: true,
                maintainAspectRatio: false,
                layout: { padding: { top: 18 } },
                plugins: {
                    legend: { display: hasLegend, labels: { color: '#ccc', font: { size: 10 } } },
                    tooltip: { backgroundColor: 'rgba(0,0,0,0.85)' },
                },
                scales: {
                    x: { ticks: { color: '#888', font: { size: 10 } }, grid: { display: false } },
                    y: {
                        title: { display: true, text: yLabel, color: '#888', font: { size: 10 } },
                        ticks: { color: '#aaa' },
                        grid: { color: 'rgba(255,255,255,0.05)' },
                    },
                },
            };
        };

        const d = this._extractConvergenciaData(convergencia, localesCount);

        // Datos de comparación
        let cmpData = null;
        let cmpName = '';
        let cmpRes = null;
        let cmpConv = null;
        if (comparacion && comparacion.resultados) {
            cmpRes = comparacion.resultados;
            cmpConv = cmpRes.convergencia || [];
            cmpName = this._getSelectDisplayName('simCompareWith') || comparacion.resolverName;
            if (cmpConv.length > 0) {
                cmpData = this._extractConvergenciaData(cmpConv, localesCount);
            }
        }

        // ═══════════════════════════════════════════════
        // Fila 1: Ganancia Casa (RTP por local)
        // ═══════════════════════════════════════════════
        const row1 = this._createChartRow(container, 0);
        const casaCtx = this._createChartSlot(
            row1,
            cmpData ? t('Ganancia Casa — ') + mainName : t('Ganancia Casa (% neto por local)'),
            320
        );
        this._addChart(casaCtx, 'line', d.labels, this._buildCasaDatasets(d, localesCount), chartOpts);

        if (cmpData) {
            const casaCmpCtx = this._createChartSlot(row1, t('Ganancia Casa — ') + cmpName, 320);
            this._addChart(
                casaCmpCtx,
                'line',
                cmpData.labels,
                this._buildCasaDatasets(cmpData, localesCount),
                chartOpts
            );
        }

        // ═══════════════════════════════════════════════
        // Fila 2: Experiencia Jugador (percentiles)
        // ═══════════════════════════════════════════════
        const row2 = this._createChartRow(container, 16);
        const jugCtx = this._createChartSlot(
            row2,
            cmpData ? t('Ganancia Jugador — ') + mainName : t('Ganancia Jugador (% neto)'),
            320
        );
        this._addChart(jugCtx, 'line', d.labels, this._buildJugadorDatasets(d), chartOpts);

        if (cmpData) {
            const jugCmpCtx = this._createChartSlot(row2, t('Ganancia Jugador — ') + cmpName, 320);
            this._addChart(jugCmpCtx, 'line', cmpData.labels, this._buildJugadorDatasets(cmpData), chartOpts);
        }

        // ═══════════════════════════════════════════════
        // Fila 3: Balance/Drawdown lado a lado
        // ═══════════════════════════════════════════════
        const row3 = this._createChartRow(container, 24);
        const dd = this._buildDrawdownDatasets(convergencia);
        const ddCtx = this._createChartSlot(
            row3,
            cmpData ? t('Balance Casa — ') + mainName : t('Balance Casa / Drawdown (%)'),
            280
        );
        this._addChart(ddCtx, 'line', dd.labels, dd.datasets, ddOpts);

        if (cmpData && cmpConv.length > 0) {
            const ddCmp = this._buildDrawdownDatasets(cmpConv);
            const ddCmpCtx = this._createChartSlot(row3, t('Balance Casa — ') + cmpName, 280);
            this._addChart(ddCmpCtx, 'line', ddCmp.labels, ddCmp.datasets, ddOpts);
        }

        // ═══════════════════════════════════════════════
        // Fila 4: Histogramas (siempre superpuestos)
        // ═══════════════════════════════════════════════
        const row4 = this._createChartRow(container, 16);

        // Histograma de pagos por sorteo
        const histograma = resultados.histogramaPagos;
        if (histograma) {
            const histLabels = [];
            const histData = [];
            for (let i = 0; i < histograma.length; i++) {
                histLabels.push(histograma[i].label);
                histData.push(histograma[i].count);
            }
            const histDatasets = [
                {
                    label: cmpRes ? mainName : 'Sorteos',
                    data: histData,
                    backgroundColor: 'rgba(102, 187, 106, 0.6)',
                    borderColor: '#66bb6a',
                    borderWidth: 1,
                },
            ];
            if (cmpRes && cmpRes.histogramaPagos) {
                const cmpHistData = [];
                for (let i = 0; i < cmpRes.histogramaPagos.length; i++)
                    cmpHistData.push(cmpRes.histogramaPagos[i].count);
                histDatasets.push({
                    label: cmpName,
                    data: cmpHistData,
                    backgroundColor: 'rgba(255, 152, 0, 0.4)',
                    borderColor: '#ff9800',
                    borderWidth: 1,
                });
            }
            const histCtx = this._createChartSlot(row4, t('Ratio Pago/Recaudo por sorteo'), 240);
            this._addChart(histCtx, 'bar', histLabels, histDatasets, barOpts('Sorteos', histDatasets.length > 1), [
                this._barValuePlugin,
            ]);
        }

        // Histograma de multiplicadores por jugada
        const histJugadas = resultados.histogramaJugadas;
        if (histJugadas) {
            const jugLabels = [];
            const jugData = [];
            for (let i = 0; i < histJugadas.length; i++) {
                jugLabels.push(histJugadas[i].label);
                jugData.push(histJugadas[i].count);
            }
            const jugDatasets = [
                {
                    label: cmpRes ? mainName : 'Jugadas',
                    data: jugData,
                    backgroundColor: 'rgba(66, 165, 245, 0.6)',
                    borderColor: '#42a5f5',
                    borderWidth: 1,
                },
            ];
            if (cmpRes && cmpRes.histogramaJugadas) {
                const cmpJugData = [];
                for (let i = 0; i < cmpRes.histogramaJugadas.length; i++)
                    cmpJugData.push(cmpRes.histogramaJugadas[i].count);
                jugDatasets.push({
                    label: cmpName,
                    data: cmpJugData,
                    backgroundColor: 'rgba(255, 152, 0, 0.4)',
                    borderColor: '#ff9800',
                    borderWidth: 1,
                });
            }
            const jugCtx2 = this._createChartSlot(row4, t('Multiplicador por jugada'), 240);
            this._addChart(jugCtx2, 'bar', jugLabels, jugDatasets, barOpts('Jugadas', jugDatasets.length > 1), [
                this._barValuePlugin,
            ]);
        }
    },

    /**
     * Obtiene el nombre legible del select para mostrar en KPIs/gráficas.
     */
    _getSelectDisplayName(selectId) {
        const select = document.getElementById(selectId);
        if (!select || !select.value) return '';
        const option = select.options[select.selectedIndex];
        return option ? option.textContent : select.value;
    },

    /**
     * Renderiza KPIs. Si hay comparación, muestra lado a lado.
     */
    // ═══════════════════════════════════════════════════════════════════════
    // KPIs — panel de métricas comparables (RTP, drawdown, casa%, etc).
    // ═══════════════════════════════════════════════════════════════════════

    _renderKpis(resultados, metadata, comparacion) {
        const kpisDiv = document.getElementById('simKpis');
        if (!kpisDiv) return;

        if (comparacion) {
            const nameA = this._getSelectDisplayName('simResolverSelect');
            const nameB = this._getSelectDisplayName('simCompareWith');
            kpisDiv.innerHTML = this._buildComparisonTable(
                nameA,
                resultados.financieros,
                nameB,
                comparacion.resultados.financieros,
                resultados.perfilPsicologico,
                comparacion.resultados.perfilPsicologico,
                metadata,
                comparacion.metadata
            );
        } else {
            kpisDiv.innerHTML =
                this._buildKpiBlock(resultados.financieros, metadata) +
                this._buildPercepcionBlock(resultados.perfilPsicologico);
        }
    },

    /**
     * Colores semáforo absolutos para KPIs individuales (perspectiva operador).
     * - Margen: 5-15% ideal, <1% o >30% peligroso
     * - Volatilidad: <0.5 predecible, >1.5 caótico
     * - Sorteos Rojo: <20% bien, >40% mal
     * - Reciclaje: >2x bueno, <1.5x malo
     * - Dinero Fresco: <50% bueno, >75% malo
     */
    _kpiColorAbsolute(kpi, value) {
        const GREEN = '#4caf50';
        const ORANGE = '#ff9800';
        const RED = '#ef5350';
        switch (kpi) {
            case 'margen': {
                const p = value * 100;
                return p >= 5 && p <= 15 ? GREEN : (p >= 1 && p < 5) || (p > 15 && p <= 30) ? ORANGE : RED;
            }
            case 'volatilidad':
                return value < 0.5 ? GREEN : value <= 1.5 ? ORANGE : RED;
            case 'sorteosRojo': {
                const p = value * 100;
                return p < 20 ? GREEN : p <= 40 ? ORANGE : RED;
            }
            case 'reciclaje':
                return value >= 2 ? GREEN : value >= 1.5 ? ORANGE : RED;
            case 'dineroFresco': {
                const p = value * 100;
                return p < 50 ? GREEN : p <= 75 ? ORANGE : RED;
            }
            case 'rachaSeca':
                return value < 8 ? GREEN : value <= 20 ? ORANGE : RED;
            case 'primerPremio':
                return value < 10 ? GREEN : value <= 25 ? ORANGE : RED;
            case 'frecuenciaExplosion':
                return value < 50 ? GREEN : value <= 150 ? ORANGE : RED;
            case 'proximidadPleno': {
                const p = value * 100;
                return p >= 5 && p <= 15 ? GREEN : ORANGE;
            }
            default:
                return null;
        }
    },

    /**
     * Colores comparativos: el mejor valor = verde, el peor = rojo/naranja.
     * higherIsBetter indica si un valor mayor es bueno para el operador.
     */
    _kpiColorCompare(valueA, valueB, higherIsBetter) {
        const GREEN = '#4caf50';
        const RED = '#ef5350';
        const NEUTRAL = 'var(--text-primary)';
        const diff = Math.abs(valueA - valueB);
        // Si la diferencia es insignificante (<1% relativo), ambos neutros
        const ref = Math.max(Math.abs(valueA), Math.abs(valueB), 0.001);
        if (diff / ref < 0.02) return { colorA: NEUTRAL, colorB: NEUTRAL };
        const aIsBetter = higherIsBetter ? valueA > valueB : valueA < valueB;
        return {
            colorA: aIsBetter ? GREEN : RED,
            colorB: aIsBetter ? RED : GREEN,
        };
    },

    _buildKpiBlock(fin, metadata) {
        const margenPct = fin.rtpGlobal * 100;
        const rojosPct = fin.sorteosEnRojoPct * 100;
        const frescoPct = fin.dineroFrescoPct * 100;

        let html =
            '<div style="font-size:11px; color:var(--text-muted); margin-bottom:4px;">' + t('Casa') + '</div>' +
            '<div class="sim-kpis-grid">' +
            this._kpiCard(margenPct.toFixed(1) + '%', t('Margen'), this._kpiColorAbsolute('margen', fin.rtpGlobal)) +
            this._kpiCard(
                fin.volatilidad.toFixed(2),
                'Volatilidad',
                this._kpiColorAbsolute('volatilidad', fin.volatilidad)
            ) +
            this._kpiCard(
                rojosPct.toFixed(0) + '%',
                t('Sorteos Rojo'),
                this._kpiColorAbsolute('sorteosRojo', fin.sorteosEnRojoPct)
            ) +
            this._kpiCard(fin.maxPremioJugada.toFixed(1) + 'x', t('Max Premio')) +
            '</div>';

        // Bloque recuperación
        if (metadata && metadata.recuperacion) {
            const rec = metadata.recuperacion;
            html += this._buildRecuperacionBlock(rec);
        }

        html +=
            '<div style="font-size:11px; color:var(--text-muted); margin-bottom:4px; margin-top:10px;">' + t('Jugador') + '</div>' +
            '<div class="sim-kpis-grid">' +
            this._kpiCard(
                fin.handleMultiplier.toFixed(1) + 'x',
                t('Reciclaje'),
                this._kpiColorAbsolute('reciclaje', fin.handleMultiplier)
            ) +
            this._kpiCard(
                frescoPct.toFixed(0) + '%',
                t('Dinero Fresco'),
                this._kpiColorAbsolute('dineroFresco', fin.dineroFrescoPct)
            ) +
            this._kpiCard((fin.rejuegoPct * 100).toFixed(0) + '%', t('Rejuego')) +
            this._kpiCard(fin.sesionMediaSorteos.toFixed(0), t('Sesión (sorteos)')) +
            this._kpiCard((fin.winRate * 100).toFixed(1) + '%', t('Win Rate')) +
            '</div>';
        return html;
    },

    _buildRecuperacionBlock(rec) {
        if (rec.recuperado) {
            const dias = rec.diasEstimados;
            const color = dias <= 5 ? '#4ade80' : dias <= 15 ? '#fbbf24' : '#f87171';
            return (
                '<div style="font-size:11px; color:var(--text-muted); margin-bottom:4px; margin-top:10px;">' + t('Recuperación') + '</div>' +
                '<div class="sim-kpis-grid">' +
                this._kpiCard('$' + rec.deficitInicial.toLocaleString(), t('Déficit')) +
                this._kpiCard(rec.sorteoRecuperacion.toLocaleString(), 'Sorteos', color) +
                this._kpiCard('~' + dias + ' días', t('Tiempo'), color) +
                '</div>'
            );
        }
        return (
            '<div style="font-size:11px; color:var(--text-muted); margin-bottom:4px; margin-top:10px;">' + t('Recuperación') + '</div>' +
            '<div class="sim-kpis-grid">' +
            this._kpiCard('$' + rec.deficitInicial.toLocaleString(), t('Déficit')) +
            this._kpiCard(t('No recuperado'), 'Sorteos', '#f87171') +
            this._kpiCard('>10,000', 'Sorteos', '#f87171') +
            '</div>'
        );
    },

    _buildPercepcionBlock(perfil) {
        if (!perfil) return '';
        return (
            '<div style="font-size:11px; color:var(--text-muted); margin-bottom:4px; margin-top:10px;">' + t('Percepción del Jugador') + '</div>' +
            '<div class="sim-kpis-grid">' +
            this._kpiCard(
                perfil.rachasSecas.mediana.toFixed(0),
                t('Racha Seca (med)'),
                this._kpiColorAbsolute('rachaSeca', perfil.rachasSecas.mediana)
            ) +
            this._kpiCard(
                perfil.rachasSecas.p90.toFixed(0),
                t('Racha Seca P90'),
                this._kpiColorAbsolute('rachaSeca', perfil.rachasSecas.p90)
            ) +
            this._kpiCard(
                perfil.primerPremio.mediana.toFixed(0),
                t('1er Premio (med)'),
                this._kpiColorAbsolute('primerPremio', perfil.primerPremio.mediana)
            ) +
            this._kpiCard(
                perfil.explosiones.frecuenciaX5.toFixed(0),
                t('Explosión 5x (freq)'),
                this._kpiColorAbsolute('frecuenciaExplosion', perfil.explosiones.frecuenciaX5)
            ) +
            this._kpiCard(
                (perfil.proximidadPleno.tasaA1 * 100).toFixed(1) + '%',
                t('Proximidad Pleno ±1'),
                this._kpiColorAbsolute('proximidadPleno', perfil.proximidadPleno.tasaA1)
            ) +
            '</div>'
        );
    },

    _buildComparisonTable(nameA, finA, nameB, finB, perfilA, perfilB, metaA, metaB) {
        // Cada row: label, valores, formato, y si higher is better para el operador
        const rows = [
            {
                label: t('Margen'),
                a: finA.rtpGlobal,
                b: finB.rtpGlobal,
                fmt: v => (v * 100).toFixed(1) + '%',
                higherIsBetter: true,
            },
            {
                label: 'Volatilidad',
                a: finA.volatilidad,
                b: finB.volatilidad,
                fmt: v => v.toFixed(2),
                higherIsBetter: false,
            },
            {
                label: t('Sorteos Rojo'),
                a: finA.sorteosEnRojoPct,
                b: finB.sorteosEnRojoPct,
                fmt: v => (v * 100).toFixed(0) + '%',
                higherIsBetter: false,
            },
            {
                label: t('Max Premio'),
                a: finA.maxPremioJugada,
                b: finB.maxPremioJugada,
                fmt: v => v.toFixed(1) + 'x',
                higherIsBetter: false,
            },
            {
                label: t('Reciclaje'),
                a: finA.handleMultiplier,
                b: finB.handleMultiplier,
                fmt: v => v.toFixed(1) + 'x',
                higherIsBetter: true,
            },
            {
                label: t('Dinero Fresco'),
                a: finA.dineroFrescoPct,
                b: finB.dineroFrescoPct,
                fmt: v => (v * 100).toFixed(0) + '%',
                higherIsBetter: false,
            },
            {
                label: t('Rejuego'),
                a: finA.rejuegoPct,
                b: finB.rejuegoPct,
                fmt: v => (v * 100).toFixed(0) + '%',
                higherIsBetter: true,
            },
        ];

        // Filas de percepción (si hay perfil psicológico)
        if (perfilA && perfilB) {
            rows.push(
                { separator: t('Percepción del Jugador') },
                {
                    label: t('Racha Seca (med)'),
                    a: perfilA.rachasSecas.mediana,
                    b: perfilB.rachasSecas.mediana,
                    fmt: v => v.toFixed(1),
                    higherIsBetter: false,
                },
                {
                    label: t('Racha Seca P90'),
                    a: perfilA.rachasSecas.p90,
                    b: perfilB.rachasSecas.p90,
                    fmt: v => v.toFixed(0),
                    higherIsBetter: false,
                },
                {
                    label: t('1er Premio (med)'),
                    a: perfilA.primerPremio.mediana,
                    b: perfilB.primerPremio.mediana,
                    fmt: v => v.toFixed(1),
                    higherIsBetter: false,
                },
                {
                    label: t('Explosión 5x (freq)'),
                    a: perfilA.explosiones.frecuenciaX5,
                    b: perfilB.explosiones.frecuenciaX5,
                    fmt: v => v.toFixed(0),
                    higherIsBetter: false,
                },
                {
                    label: t('Proximidad Pleno ±1'),
                    a: perfilA.proximidadPleno.tasaA1,
                    b: perfilB.proximidadPleno.tasaA1,
                    fmt: v => (v * 100).toFixed(1) + '%',
                    higherIsBetter: null,
                }
            );
        }

        // Filas de recuperación (si hay datos de recuperación)
        const recA = metaA && metaA.recuperacion;
        const recB = metaB && metaB.recuperacion;
        if (recA || recB) {
            const fmtRec = rec => {
                if (!rec) return '-';
                if (!rec.recuperado) return t('No recuperado');
                return rec.sorteoRecuperacion.toLocaleString() + ' (~' + rec.diasEstimados + 'd)';
            };
            rows.push(
                { separator: 'Recuperación ($' + ((recA || recB).deficitInicial || 0).toLocaleString() + ' déficit)' },
                {
                    label: t('Sorteos para recuperar'),
                    a: recA ? (recA.recuperado ? recA.sorteoRecuperacion : 99999) : 99999,
                    b: recB ? (recB.recuperado ? recB.sorteoRecuperacion : 99999) : 99999,
                    fmtA: () => fmtRec(recA),
                    fmtB: () => fmtRec(recB),
                    higherIsBetter: false,
                }
            );
        }

        let html = '<table style="width:100%; border-collapse:collapse; font-size:13px;">';
        html += '<thead><tr>';
        html +=
            '<th style="text-align:left; padding:6px 8px; border-bottom:1px solid var(--border-color); color:var(--text-muted);"></th>';
        html +=
            '<th style="text-align:center; padding:6px 8px; border-bottom:1px solid var(--border-color); color:var(--text-primary); text-transform:uppercase; font-size:11px; letter-spacing:0.5px;">' +
            nameA +
            '</th>';
        html +=
            '<th style="text-align:center; padding:6px 8px; border-bottom:1px solid var(--border-color); color:var(--text-primary); text-transform:uppercase; font-size:11px; letter-spacing:0.5px;">' +
            nameB +
            '</th>';
        html += '</tr></thead><tbody>';

        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            if (r.separator) {
                html +=
                    '<tr><td colspan="3" style="padding:10px 8px 4px; font-size:11px; color:var(--text-muted); border-top:1px solid var(--border-color); text-transform:uppercase; letter-spacing:0.5px;">' +
                    r.separator +
                    '</td></tr>';
                continue;
            }
            let colorA = 'var(--text-primary)';
            let colorB = 'var(--text-primary)';
            if (r.higherIsBetter !== null && r.higherIsBetter !== undefined) {
                const colors = this._kpiColorCompare(r.a, r.b, r.higherIsBetter);
                colorA = colors.colorA;
                colorB = colors.colorB;
            }
            const valA = r.fmtA ? r.fmtA() : r.fmt(r.a);
            const valB = r.fmtB ? r.fmtB() : r.fmt(r.b);
            html += '<tr>';
            html += '<td style="padding:5px 8px; color:var(--text-secondary);">' + r.label + '</td>';
            html +=
                '<td style="padding:5px 8px; text-align:center; font-weight:600; color:' +
                colorA +
                ';">' +
                valA +
                '</td>';
            html +=
                '<td style="padding:5px 8px; text-align:center; font-weight:600; color:' +
                colorB +
                ';">' +
                valB +
                '</td>';
            html += '</tr>';
        }

        html += '</tbody></table>';
        return html;
    },

    _fmtMoney(n) {
        if (n == null) return '-';
        const abs = Math.abs(n);
        const sign = n < 0 ? '-' : '';
        if (abs >= 1000000) return sign + '$' + (abs / 1000000).toFixed(1) + 'M';
        if (abs >= 10000) return sign + '$' + (abs / 1000).toFixed(0) + 'K';
        if (abs >= 1000) return sign + '$' + (abs / 1000).toFixed(1) + 'K';
        return sign + '$' + abs.toFixed(0);
    },

    _fmtNum(n) {
        if (!n && n !== 0) return '-';
        if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
        if (n >= 10000) return (n / 1000).toFixed(0) + 'K';
        if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
        return String(n);
    },

    // Tarjeta KPI canónica (.kpi-card de 07-cards.css); el ajuste de tamaño para la
    // grilla compacta del simulador vive scoped en .sim-kpis-grid (10-command-center.css).
    _kpiCard(value, label, color) {
        const colorStyle = color ? ' style="color:' + color + ';"' : '';
        return (
            '<div class="kpi-card"><div class="kpi-label">' +
            label +
            '</div><div class="kpi-value"' +
            colorStyle +
            '>' +
            value +
            '</div></div>'
        );
    },
};
